import { useEffect, useRef, useState } from "react";

const APP_ID = process.env.REACT_APP_SQUARE_APP_ID || "";
const LOCATION_ID = process.env.REACT_APP_SQUARE_LOCATION_ID || "";
const SDK_URL = APP_ID.startsWith("sandbox-sq")
    ? "https://sandbox.web.squarecdn.com/v1/square.js"
    : "https://web.squarecdn.com/v1/square.js";

export function useSquareCard(containerId) {
    const [ready, setReady] = useState(false);
    const [configured] = useState(Boolean(APP_ID && LOCATION_ID));
    const [error, setError] = useState(null);
    const cardRef = useRef(null);

    useEffect(() => {
        if (!configured) return;

        let cancelled = false;

        const init = async () => {
            try {
                const payments = await window.Square.payments(APP_ID, LOCATION_ID);
                const card = await payments.card();
                await card.attach(`#${containerId}`);
                if (!cancelled) {
                    cardRef.current = card;
                    setReady(true);
                }
            } catch (e) {
                if (!cancelled) setError(e.message);
            }
        };

        const existingScript = document.getElementById("sq-web-sdk");
        if (existingScript && window.Square) {
            init();
        } else if (!existingScript) {
            const s = document.createElement("script");
            s.id = "sq-web-sdk";
            s.src = SDK_URL;
            s.onload = init;
            s.onerror = () => setError("Failed to load Square SDK.");
            document.head.appendChild(s);
        } else {
            existingScript.addEventListener("load", init, { once: true });
        }

        return () => {
            cancelled = true;
            cardRef.current?.destroy?.();
            cardRef.current = null;
        };
    }, [containerId, configured]);

    const tokenize = async () => {
        if (!cardRef.current) throw new Error("Card form not ready.");
        const result = await cardRef.current.tokenize();
        if (result.status === "OK") return result.token;
        throw new Error(result.errors?.[0]?.message || "Card declined. Please try again.");
    };

    return { ready, configured, error, tokenize };
}

export default function SquareCardForm({ containerId = "sq-card-container", className = "" }) {
    const { configured, error } = useSquareCard(containerId);

    if (!configured) {
        return (
            <div className={`rounded-lg border border-dashed border-white/20 p-6 text-center bg-black/20 ${className}`}>
                <div className="text-sm text-white/60 leading-relaxed">
                    Online payment coming soon.{" "}
                    <a href="tel:8623032001" className="text-orange-300 hover:underline">
                        Call (862) 303-2001
                    </a>{" "}
                    to pre-order or pay a deposit.
                </div>
            </div>
        );
    }

    return (
        <div className={className}>
            <div id={containerId} className="sq-card-element" />
            {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
        </div>
    );
}
